import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Search, MapPin } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useMeta } from "../lib/seo";
import { Input } from "../components/ui/input";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function Regulators() {
  useMeta("Where to File a Warranty Complaint in Your State",
    "Look up which state office handles complaints about home warranty and service contract companies, and what to include when you file.");
  const { user } = useAuth();
  const [regulators, setRegulators] = useState([]);
  const [q, setQ] = useState("");
  const [userState, setUserState] = useState(null);

  useEffect(() => { api.get("/regulators").then((r) => setRegulators(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!user) return;
    api.get("/claims").then((r) => {
      const withState = r.data.find((c) => c.state);
      if (withState) setUserState(withState.state);
    }).catch(() => {});
  }, [user]);

  const { national, states } = useMemo(() => {
    const nat = regulators.filter((r) => r.state.startsWith("US-") && r.state !== "US-HHS");
    const st = regulators
      .filter((r) => !r.state.startsWith("US-"))
      .sort((a, b) => a.state_name.localeCompare(b.state_name));
    return { national: nat, states: st };
  }, [regulators]);

  const filtered = useMemo(() => states.filter((r) =>
    !q || r.state_name.toLowerCase().includes(q.toLowerCase()) || r.state.toLowerCase().includes(q.toLowerCase())),
    [states, q]);

  const availableLetters = useMemo(() => new Set(filtered.map((r) => r.state_name[0])), [filtered]);
  const yourState = userState ? states.find((r) => r.state === userState) : null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-16 fade-in" data-testid="regulators-page">
      <h1 className="text-4xl font-extrabold tracking-tight mb-2">State regulators directory</h1>
      <p className="text-slate-600 mb-8">Where to file complaints when a company won&rsquo;t budge. Home warranty companies are regulated as service-contract providers in most states — your insurance commissioner or service-contract regulator handles complaints.</p>

      {yourState && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 mb-8" data-testid="your-state-card">
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 flex items-center gap-1 mb-2"><MapPin size={14} /> Your state</p>
          <StateCard r={yourState} />
        </div>
      )}

      <h2 className="text-xl font-bold mb-1">National / federal</h2>
      <p className="text-sm text-slate-500 mb-4">These apply in every state — file here in addition to your state regulator.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10" data-testid="national-regulators">
        {national.map((r) => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`regulator-card-${r.state}`}>
            <p className="font-bold">{r.state_name}</p>
            <a href={r.insurance_url} target="_blank" rel="noreferrer"
              className="mt-2 inline-flex items-center gap-2 text-emerald-700 font-medium hover:underline text-sm"
              data-testid={`regulator-link-${r.state}`}>
              {{ "US-FTC": "Report fraud to the FTC", "US-BBB": "File a BBB complaint" }[r.state] || "File a complaint"} <ExternalLink size={13} />
            </a>
          </div>
        ))}
      </div>

      <div className="sticky top-16 z-30 bg-background/95 backdrop-blur-sm py-3 mb-8 -mx-4 px-4" data-testid="jump-bar-wrap">
        <div className="relative mb-3 max-w-md">
          <Search size={16} className="absolute left-3 top-3 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by state name or abbreviation…" className="pl-9" data-testid="regulator-search-input" />
        </div>
        <div className="flex gap-1 flex-wrap" data-testid="az-jump-bar">
          {LETTERS.map((l) => (
            <a key={l} href={availableLetters.has(l) ? `#state-${l}` : undefined}
              className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded ${availableLetters.has(l) ? "text-emerald-700 hover:bg-emerald-100" : "text-slate-300 pointer-events-none"}`}
              data-testid={`jump-${l}`}>{l}</a>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="regulators-list">
        {filtered.map((r) => (
          <div key={r.id} id={`state-${r.state_name[0]}`} className="scroll-mt-40">
            <StateCard r={r} />
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500 mt-8">Links are provided for convenience — verify complaint procedures on the regulator&rsquo;s site before filing. Something missing or outdated? Let us know and we&rsquo;ll update the directory.</p>
    </div>
  );
}

function StateCard({ r }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 h-full" data-testid={`regulator-card-${r.state}`}>
      <p className="font-bold">{r.state_name} <span className="text-slate-500 font-medium">({r.state})</span></p>
      <div className="mt-3 space-y-2 text-sm">
        <a href={r.insurance_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-emerald-700 font-medium hover:underline" data-testid={`regulator-insurance-${r.state}`}>
          {r.insurance_name} <ExternalLink size={13} />
        </a>
        <a href={r.ag_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-emerald-700 font-medium hover:underline" data-testid={`regulator-ag-${r.state}`}>
          {r.ag_name} <ExternalLink size={13} />
        </a>
      </div>
    </div>
  );
}
